import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useState } from 'react'
import Dialog from '../../atoms/feedback/Dialog'
import Button from '../../atoms/inputs/Button'
import { CompanySelectors } from '../../../store/company/selectors'
import Avatar from '../../atoms/display/Avatar'
import Label from '../../atoms/display/Label'
import { UserAddIcon, UserRemoveIcon } from '../../atoms/display/Icons'
import IconButton from '../../atoms/inputs/IconButton'
import { RelatedUser, toRelatedUser, User } from '../../../types/User'
import { FlexColumn, FlexRow } from '../../atoms/layout/Flex'
import Divider from '../../atoms/display/Divider'
import Alert from '../../atoms/feedback/Alert'
import { CompanyAgreementSettings } from '../../../types/Company'
import AvatarUserStepper from '../../molecules/display/AvatarUserStepper'
import { CompanyActions } from '../../../store/company'

type Props = {
  open: boolean
  settings: CompanyAgreementSettings
  onClose: () => void
}

export default function AgreementSettingDialog({ open, settings, onClose }: Props) {
  const users = useSelector(CompanySelectors.users)
  const company = useSelector(CompanySelectors.company)
  const [selectedUsers, setSelectedUsers] = useState<RelatedUser[]>(settings.users.map((user) => user.user))
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const dispatch = useDispatch()

  const handleAddUser = useCallback(
    (user: User) => {
      if (selectedUsers.length >= 5) {
        return
      }
      setSelectedUsers([...selectedUsers, toRelatedUser(user)])
    },
    [selectedUsers, setSelectedUsers]
  )
  const handleRemoveUser = useCallback(
    (user: User) => {
      setSelectedUsers(selectedUsers.filter((selectedUser) => selectedUser.id !== user.id))
    },
    [selectedUsers, setSelectedUsers]
  )
  const handleSave = useCallback(() => {
    setErrorMessage(null)
    if (selectedUsers.find((user) => user.id === company?.representative?.userId) === undefined) {
      setErrorMessage('契約書の承認フローには代表ユーザーを含めてください')
      return
    }
    dispatch(CompanyActions.saveAgreementSettings({ users: selectedUsers, onSuccess: onClose }))
  }, [dispatch, selectedUsers, onClose, setErrorMessage, company])

  return (
    <Dialog
      open={open}
      title="契約書承認フローの設定"
      width="lg"
      onClose={onClose}
      actions={
        <FlexColumn align="flex-end">
          {errorMessage && <Label text={errorMessage} color="error" />}
          <Button disabled={selectedUsers.length === 0} onClick={handleSave}>
            保存
          </Button>
        </FlexColumn>
      }
    >
      <FlexColumn space={2}>
        <FlexColumn>
          {selectedUsers.length > 0 ? (
            <AvatarUserStepper users={selectedUsers} />
          ) : (
            <Alert severity="warning" label="メンバーを追加してください" />
          )}
        </FlexColumn>
        <Divider />
        <FlexColumn>
          <Label text="フローには5人まで追加できます" variant="caption" />
          <FlexColumn space={2}>
            {users.map((user) => (
              <FlexRow key={user.id} align="center" justify="space-between">
                <FlexRow align="center" width="240px">
                  <Avatar alt={user.name} url={user.icon} />
                  <Label text={user.name} />
                </FlexRow>
                {selectedUsers.map((selectedUser) => selectedUser.id).includes(user.id) ? (
                  <IconButton label="" onClick={() => handleRemoveUser(user)} color="caution">
                    <UserRemoveIcon />
                  </IconButton>
                ) : (
                  <IconButton label="" onClick={() => handleAddUser(user)} disabled={selectedUsers.length >= 5}>
                    <UserAddIcon />
                  </IconButton>
                )}
              </FlexRow>
            ))}
          </FlexColumn>
        </FlexColumn>
      </FlexColumn>
    </Dialog>
  )
}
